'use client';

import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { useCategories, usePopularTags } from '@/lib/api/event/hooks';
import type { CategoryItem, PopularTag } from '@/lib/api/event/models';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { useEventsState } from '@/lib/components/EventsContext';
import { filters } from '@/lib/types/events';
import { AnimatedIcon } from '@/lib/ui-kit/animated-icon';
import { Icon } from '@/lib/ui-kit/icons';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { Checkbox } from '@/lib/ui-kit-v2/checkbox';
import { FilterDropdown } from '@/lib/ui-kit-v2/filter-dropdown';
import { TabGroup } from '@/lib/ui-kit-v2/tabs';
import {
  categoryFromSearchParams,
  pushTagToUrl,
  resolveTagFromSlug,
  TAG_PARAM,
} from '@/lib/utils/tagQuery';

interface EventsFilterCategoriesProps {
  initialCategories?: CategoryItem[];
  initialTags?: PopularTag[];
  initialRootSlug?: string;
}

export const EventsFilterCategories = ({
  initialCategories,
  initialTags,
  initialRootSlug,
}: EventsFilterCategoriesProps) => {
  const t = useTranslations('eventsFilter');
  const searchParams = useSearchParams();

  const sortByLabels: Record<string, string> = {
    RECOMMENDED: t('sortOptions.recommended'),
    VOLUME_24H: t('sortOptions.24hVolume'),
    CHANCE: t('sortOptions.chance'),
    TOTAL_VOLUME: t('sortOptions.totalVolume'),
    LIQUIDITY: t('sortOptions.liquidity'),
    NEWEST: t('sortOptions.newestFirst'),
    OLDEST: t('sortOptions.oldestFirst'),
    ENDING_SOON: t('sortOptions.endingSoon'),
  };

  const endingLabels: Record<string, string> = {
    ALL: t('endingOptions.all'),
    ONE_HOUR: t('endingOptions.1hour'),
    TODAY: t('endingOptions.today'),
    SEVEN_DAYS: t('endingOptions.7days'),
    THIRTY_DAYS: t('endingOptions.30days'),
    MORE_THAN_30_DAYS: t('endingOptions.more30days'),
  };
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthContext();
  const { data: categories } = useCategories(initialCategories);

  // Root category is a bare key ("crypto" from "/?crypto"); the tag rides
  // along as `?tag=<slug>` and must not be mistaken for a category.
  const activeRootSlug = categoryFromSearchParams(searchParams.entries());
  const hasSecondaryNavCategory = activeRootSlug !== '';
  const tagSlugFromUrl = searchParams.get(TAG_PARAM) ?? '';

  // Resolve slug → id for the subcategories query
  const activeRootId = useMemo(() => {
    if (!categories || !activeRootSlug) return undefined;
    return categories.find((c) => c.slug === activeRootSlug)?.id;
  }, [categories, activeRootSlug]);

  // Only seed the cache with SSR tags when the current slug matches the SSR slug —
  // prevents crypto tags from seeding the sports cache during SPA navigation
  const scopedInitialTags = activeRootSlug === initialRootSlug ? initialTags : undefined;

  // Fetch popular tags — with categoryId when a root is selected, without for "All"
  const { data: tags, isLoading: isTagsLoading } = usePopularTags(
    activeRootId,
    20,
    scopedInitialTags,
  );

  const hideOptions = useMemo(
    () => categories?.map((c) => ({ value: c.slug ?? c.id, label: c.name })) ?? undefined,
    [categories],
  );

  // Category and filters live in client state only
  const {
    setCategory,
    tag,
    setTag,
    ending,
    setEnding,
    hide,
    setHide,
    sortBy,
    setSortBy,
    setShowMyMarkets,
    showMyMarkets,
    setSortByDirection,
    sortByDirection,
    initialEnding,
    initialHide,
    initialSortBy,
    initialShowMyMarkets,
    showFavorites,
    setShowFavorites,
    recommendedEventIds,
  } = useEventsState();

  const tagTabs = useMemo(() => {
    if (!tags || tags.length === 0) return [];
    const toTab = (name: string) => ({
      value: name,
      label: name.charAt(0).toUpperCase() + name.slice(1),
    });
    const tabs = [
      { value: activeRootSlug || 'all', label: t('allTags') },
      ...tags.map((tg) => toTab(tg.name)),
    ];
    // A tag opened from a link may be outside the popular list — surface it
    // right after "All" so the active filter shows.
    if (tag && !tabs.some((tb) => tb.value === tag)) {
      tabs.splice(1, 0, toTab(tag));
    }
    return tabs;
  }, [tags, activeRootSlug, tag, t]);

  // When root category changes, reset subcategory selection to root slug (or 'all').
  useEffect(() => {
    setCategory(activeRootSlug || 'all');
  }, [activeRootSlug, setCategory]);

  // The URL owns the tag: a shared `/?tag=fed`, the back button and a chip
  // click all land here. Slugs are resolved against the loaded tag list, so
  // wait for it before guessing at a deep-linked tag's name.
  useEffect(() => {
    if (!tagSlugFromUrl) {
      setTag('');
      return;
    }
    if (isTagsLoading) return;
    setTag(resolveTagFromSlug(tagSlugFromUrl, tags));
  }, [tagSlugFromUrl, tags, isTagsLoading, setTag]);

  const [isShowFilters, setIsShowFilters] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const hasFiltersChanged = useMemo(() => {
    return (
      sortBy !== initialSortBy ||
      showMyMarkets !== initialShowMyMarkets ||
      ending !== initialEnding ||
      hide.length !== initialHide.length
    );
  }, [
    sortBy,
    initialSortBy,
    showMyMarkets,
    initialShowMyMarkets,
    ending,
    initialEnding,
    hide,
    initialHide,
  ]);

  const handleTabChange = (id: string) => {
    const isAllTab = id === activeRootSlug || id === 'all';
    const nextTag = isAllTab ? '' : id;
    setTag(nextTag);
    pushTagToUrl(activeRootSlug, nextTag);
  };

  const handleToggleFilters = () => setIsShowFilters((prev) => !prev);

  const handleToggleFavorites = () => setShowFavorites(!showFavorites);

  const sortByOptions = filters.sortBy && 'options' in filters.sortBy ? filters.sortBy.options : [];
  const endingOptions = filters.ending && 'options' in filters.ending ? filters.ending.options : [];

  const currentSortable = sortByOptions.find((o) => o.value === sortBy)?.sortable ?? false;
  const directionLabel = sortByDirection === 'DESC' ? t('highToLow') : t('lowToHigh');

  // "Recommended" only makes sense when personalization actually returned ids;
  // otherwise it would be an identical no-op next to "24h Volume", so hide it.
  const sortDropdownOptions = useMemo(
    () =>
      sortByOptions
        .filter((o) => o.value !== 'RECOMMENDED' || recommendedEventIds.length > 0)
        .map((o) => ({
          value: o.value,
          label: sortByLabels[o.value] ?? o.label,
        })),
    [sortByOptions, sortByLabels, recommendedEventIds.length],
  );

  const endingDropdownOptions = useMemo(
    () =>
      endingOptions.map((o) => ({
        value: o.value,
        label: endingLabels[o.value] ?? o.label,
      })),
    [endingOptions, endingLabels],
  );

  const hideDropdownOptions = useMemo(
    () => (hideOptions ?? []).map((o) => ({ value: o.value, label: o.label })),
    [hideOptions],
  );

  const handleSortChange = (value: string) => {
    if (value === sortBy) {
      const sortable = sortByOptions.find((o) => o.value === value)?.sortable;
      if (sortable) {
        setSortByDirection(sortByDirection === 'DESC' ? 'ASC' : 'DESC');
      }
      return;
    }
    setSortBy(value);
    const opt = sortByOptions.find((o) => o.value === value);
    if (opt?.sortable) setSortByDirection('DESC');
    else if (opt?.defaultDirection) setSortByDirection(opt.defaultDirection);
  };

  const hideTriggerLabel = useMemo(() => {
    if (hide.length === 0) return t('nothing');
    const matched = hide
      .map((v) => hideDropdownOptions.find((o) => o.value === v)?.label)
      .filter(Boolean);
    if (matched.length <= 2) return matched.join(', ');
    return `${matched.length} selected`;
  }, [hide, hideDropdownOptions, t]);

  const handleClearAll = () => {
    setSortBy(initialSortBy);
    setEnding(initialEnding);
    setHide(initialHide);
    setShowMyMarkets(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label="Toggle filters"
            aria-expanded={isShowFilters}
            onClick={handleToggleFilters}
            className={cn(
              'flex size-9 items-center justify-center rounded-[10px] text-v2-text-secondary transition-colors',
              'hover:bg-v2-btn-secondary hover:text-v2-text-primary',
              isShowFilters && 'bg-v2-btn-secondary text-v2-text-primary',
            )}
          >
            <AnimatedIcon
              name="settings-filter"
              size={20}
              playOnParentHover
              className="lottie-current"
            />
          </button>
          <button
            type="button"
            aria-label="Toggle favorites"
            onClick={handleToggleFavorites}
            className={cn(
              'flex size-9 items-center justify-center rounded-[10px] text-v2-icon-primary transition-colors',
              'hover:bg-v2-btn-secondary hover:text-v2-text-primary',
              showFavorites && 'bg-v2-btn-secondary',
            )}
          >
            {showFavorites ? (
              <Icon name="BookmarkFilled" size={20} />
            ) : (
              <AnimatedIcon
                name="bookmark"
                size={20}
                playOnParentHover
                className="lottie-current"
              />
            )}
          </button>
        </div>
        {isTagsLoading ? (
          <div className="scrollbar-hide flex min-w-0 flex-1 items-center gap-1 overflow-x-auto overflow-y-hidden">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-20 rounded-[10px]" />
            ))}
          </div>
        ) : tagTabs.length > 0 ? (
          <TabGroup
            scrollable
            items={tagTabs}
            value={tag || activeRootSlug || 'all'}
            onValueChange={handleTabChange}
          />
        ) : null}
      </div>
      {/*
        Two tracks. The grid row animates the row's *real* height (0fr → 1fr), so
        the panel can grow — another dropdown, "Clear all" — without a magic
        max-height that spends half the transition on nothing. The row then fades
        in just behind it, so the space opens first and the controls arrive into
        it. Opening earns a beat; closing gets out of the way.

        No transform on the content: the row is clipped from below, so anything
        that slid on the way in would have to climb out on the way back.
      */}
      <div
        aria-hidden={!isShowFilters}
        inert={!isShowFilters}
        className={cn(
          'grid transition-[grid-template-rows] motion-reduce:transition-none!',
          isShowFilters
            ? 'grid-rows-[1fr] duration-[260ms] ease-out'
            : 'grid-rows-[0fr] duration-[180ms] ease-in',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={cn(
              'scrollbar-hide flex items-center gap-4 overflow-x-auto px-1 py-1 [&>*]:shrink-0',
              'transition-opacity motion-reduce:transition-none!',
              isShowFilters
                ? 'opacity-100 delay-[80ms] duration-[180ms] ease-out'
                : 'opacity-0 delay-0 duration-[110ms] ease-out',
            )}
          >
            <FilterDropdown
              prefix={<span className="text-v2-text-secondary">{t('sortBy')}:&nbsp;</span>}
              label={
                <>
                  <span className="text-v2-text-secondary">{t('sortBy')}:&nbsp;</span>
                  <span>
                    {sortByLabels[sortBy] ?? sortBy}
                    {currentSortable && ` (${directionLabel})`}
                  </span>
                </>
              }
              options={sortDropdownOptions}
              value={sortBy}
              onValueChange={handleSortChange}
            />
            <FilterDropdown
              prefix={<span className="text-v2-text-secondary">{t('ending')}:&nbsp;</span>}
              options={endingDropdownOptions}
              value={ending}
              onValueChange={setEnding}
            />

            {!hasSecondaryNavCategory && hideDropdownOptions.length > 0 && (
              <FilterDropdown
                multiple
                prefix={<span className="text-v2-text-secondary">{t('hide')}:&nbsp;</span>}
                label={
                  <>
                    <span className="text-v2-text-secondary">{t('hide')}:&nbsp;</span>
                    <span>{hideTriggerLabel}</span>
                  </>
                }
                options={hideDropdownOptions}
                value={hide}
                onValueChange={setHide}
              />
            )}

            {mounted && !isAuthLoading && isAuthenticated && (
              <Checkbox
                checked={showMyMarkets}
                onCheckedChange={(v) => setShowMyMarkets(v === true)}
                label={t('showMyMarketsOnly')}
              />
            )}
            {hasFiltersChanged && (
              <Button appearance="ghost" size="S" onClick={handleClearAll}>
                {t('clearAll')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
